<template>
    <div ref="plot">
    </div>
</template>

<script>
import * as d3 from "d3";
import _ from "lodash";
import { drawVerticalLines } from '../util';
export default {
    props: ['input', 'meta', 'lineCoordinates'],
    watch: {
        input() {
            this.selectedRow = null;
            this.render();
        }
    },
    data() {
      return {
          selectedRow: null,
      }
    },
    methods: {

        render() {
            const el = this.$refs['plot'];
            el.innerHTML = ''; // clear

            if (this.input) {
                // Define colors
                const typeColors = {"exon": "royalblue", "cds": "lightblue"};

                let maxRank = d3.max(this.input.molecular_trait_ranks);

                // Sort data based on ranks
                let data = this.input.molecular_trait_ids.map((label, i) => ({
                    label: label,
                    start: this.input.start[i],
                    end: this.input.end[i],
                    // rank: this.input.molecular_trait_ranks[i],
                    rank: maxRank + 1 - this.input.molecular_trait_ranks[i],
                    type: this.input.feature_type[i],
                }));
                // data.sort((a, b) => a.rank - b.rank);
                data.sort((a, b) => a.rank - b.rank || a.start - b.start);

                // Define y scale based on ranks
                let y = d3.scaleBand()
                    .domain(d3.range(d3.max(this.input.molecular_trait_ranks)))
                    .range([0, maxRank * 50])
                    .padding(0.3);

                const margin = {top: 30, right: 30, bottom: 60, left: 60},
                    width = 1000 - margin.left - margin.right,
                    height = y.range()[1];

                const svg = d3.select(el)
                    .append("svg")
                    .attr("width", width + margin.left + margin.right)
                    .attr("height", height + margin.top + margin.bottom)
                    .append("g")
                    .attr("transform", `translate(${margin.left}, ${margin.top})`);

                const x = d3.scaleLinear()
                    .domain([0, this.meta.x_limit])
                    .range([ 0, width ]);

                svg.append("g")
                    .attr("transform", `translate(0, ${height})`)
                    .call(d3.axisBottom(x));


                // Draw lines between boxes
                data.forEach((d, i) => {
                    if (i < data.length - 1 && data[i+1].rank === d.rank) {
                        svg.append("line")
                            .attr("x1", x(d.end))
                            .attr("x2", x(data[i+1].start))
                            .attr("y1", y(d.rank - 1) + 10)
                            .attr("y2", y(d.rank - 1) + 10)
                            .style("stroke", typeColors[d.type])
                            .style("stroke-width", "1px");
                    }
                });

                // Draw boxes
                let boxes = svg.selectAll(".box")
                    .data(data)
                    .enter().append("rect")
                    .attr("class", "box")
                    .attr("x", d => x(d.start))
                    .attr("y", d => y(d.rank - 1))
                    .attr("width", d => x(d.end) - x(d.start))
                    .attr("height", '20')
                    .style("fill", d => typeColors[d.type]);

                // Draw labels
                data.forEach((d, i) => {
                    if (i === 0 || data[i-1].rank !== d.rank) {
                        const text = svg.append("text")
                            .attr("x", x(d.start))
                            .attr("y", y(d.rank - 1))
                            .attr("dy", -5)
                            .attr("font-weight", d.rank === this.selectedRow ? "bold" : "normal")
                            .text(d.label);

                        const textWidth = text.node().getComputedTextLength();

                        if (x(d.start) + textWidth > width) {
                            text.attr("x", x(d.start) - (x(d.start) + textWidth - width + 5));  // additional 5 for padding
                        }
                    }
                });

                data.forEach((d, i) => {
                    if ((i === 0 || data[i-1].rank !== d.rank) && !d.label.startsWith("GENE:")) {
                        svg.append("rect")
                            .attr("x", 0)
                            .attr("y", y(d.rank - 1) - y(0))
                            .attr("width", width)
                            .attr("height", y(1) - y(0))
                            .attr("tabindex", 0)
                            .attr("class", d.rank === this.selectedRow ? "focused" : "")
                            .style("fill-opacity", 0)
                            .style("cursor", "pointer")
                            .on("click", () => {
                                this.selectedRow = d.rank;
                                this.$emit('newLineCoordinates', data.filter(item => item.rank === d.rank && item.type === "exon"));
                                this.$emit('annoSelected', d);
                                this.render();
                            })
                            .on("keypress", (e)=>{
                                if(e.keyCode === 13) {
                                    this.selectedRow = d.rank;
                                    this.$emit('annoSelected', d);
                                    this.$emit('newLineCoordinates', data.filter(item => item.rank === d.rank && item.type === "exon"));
                                    this.render();
                                }
                            });
                    }
                });

                try {d3.select("rect.focused").node().focus();} catch {};

                // Add x axis label
                svg.append("text")
                    .attr("transform", "translate(" + (width/2) + " ," + (height + margin.top + 20) + ")")
                    .style("text-anchor", "middle")
                    .text("Distance from region start (bp)");

                // Add facet label
                svg.append("rect")
                    .attr("x", width)
                    .attr("y", 0)
                    .attr("width", 75)
                    .attr("height", height)
                    .style("fill", "#f0f0f0");

                if (this.selectedRow !== null) {
                    const selectedData = _.find(data, { rank: this.selectedRow });

                    svg.append("text")
                        .attr("x", x(this.meta.x_limit))
                        .attr("y", y(selectedData.rank - 1) + 10)
                        .attr("dy", 5)
                        .attr("dx", 20)
                        .attr("text-anchor", "end")
                        .text("<")
                        .style("font-weight", "bold");
                }
                // Checks if the quantification method is 'leafcutter' and there are line coordinates available. 
                // If True, it calls `drawVerticalLines` to render vertical lines on the SVG element.
                if (this.meta.quant_method === "leafcutter" && this.lineCoordinates.length > 0) {
                    drawVerticalLines(svg, x, height, this.lineCoordinates)
                }
            }
        }
    }
}
</script>

<style>
rect:focus {
    outline: 3px solid rgba(173, 188, 230, 0.7);
}
rect.focused:focus {
    outline: none;
}
</style>